import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import WellData from '../components/WellData';
import AttachmentManager from '../components/AttachmentManager';
import './Casing_v2.css';

// Stable configuration objects to prevent unnecessary re-renders
const CASING_ALLOWED_FILE_TYPES = ['image/*', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'];
const CASING_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const Casing_v2 = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [wellId, setWellId] = useState(null);

    // Get wellId from URL parameters on component mount
    useEffect(() => {
        const urlWellId = searchParams.get('wellId');
        if (urlWellId) {
            setWellId(urlWellId);
        }
    }, [searchParams]);

    // Additional state for casing-specific features
    const [casingDiagram, setCasingDiagram] = useState(null);
    const [showDiagramModal, setShowDiagramModal] = useState(false);
    const [tallyStats, setTallyStats] = useState({
        totalJoints: 0,
        totalLength: 0,
        averageLength: 0,
        totalWeight: 0
    });

    // Configuration for the WellData component
    const wellDataConfig = {
        tables: { parent: 'CasingRun', child: 'CasingTally' },
        identityColumns: { parent: 'CasingRunID', child: 'TallyID' },
        matchKey: { parentKey: 'CasingRunID', childForeignKey: 'CasingRunID' },
        title: 'Well Casing',
        icon: '🔧',
        parentIcon: '📋',
        childIcon: '🔗',
        emptyParentMessage: 'No casing runs found for this well',
        emptyChildMessage: 'No tally records found for this casing run',
        // Display configuration
        showTabs: true,
        tabs: [
            { key: 'details', label: 'Casing Tally', icon: '🔗' },
            { key: 'attachments', label: 'Attachments', icon: '📎' }
        ],
        // Custom field mappings
        parentDisplayFields: ['RunDate'],
        // Column filters
        hiddenParentFields: ['WellID'],
        hiddenChildFields: ['CasingRunID'],
        // Additional features
        enableInlineEdit: true,
        enableImageUpload: false,
        enableAttachments: true
    };

    // Handle parent (CasingRun) selection - calculate tally statistics
    const handleCasingRunSelect = useCallback(async (casingRun) => {
        console.log('🔧 Casing Run selected:', casingRun);
        
        // Calculate statistics when a casing run is selected
        if (casingRun && casingRun.CasingRunID) {
            try {
                console.log('📊 Calculating tally statistics for run:', casingRun.CasingRunID);
                // In a real implementation, you might calculate stats from tally data
                // For now, we'll set placeholder values
                setTallyStats({
                    totalJoints: 0,
                    totalLength: 0,
                    averageLength: 0,
                    totalWeight: 0
                });
            } catch (error) {
                console.error('Failed to calculate tally statistics:', error);
            }
        }
    }, []);

    // Handle child (CasingTally) selection
    const handleTallySelect = useCallback((tally) => {
        console.log('🔗 Tally record selected:', tally);
        // Add any specific handling for tally selection
    }, []);

    // Handle casing run save - refresh statistics
    const handleCasingRunSave = useCallback(async (savedRun) => {
        console.log('💾 Casing run saved:', savedRun);
        // Refresh statistics after save
        if (savedRun && savedRun.CasingRunID) {
            await handleCasingRunSelect(savedRun);
        }
    }, [handleCasingRunSelect]);

    // Handle tally save - update statistics
    const handleTallySave = useCallback(async (savedTally, parentRun) => {
        console.log('💾 Tally record saved:', savedTally);
        // Recalculate statistics when tally changes
        if (parentRun) {
            await handleCasingRunSelect(parentRun);
        }
    }, [handleCasingRunSelect]);

    // Handle delete operations
    const handleCasingRunDelete = useCallback((deletedRun) => {
        console.log('🗑️ Casing run deleted:', deletedRun);
        // Reset statistics when run is deleted
        setTallyStats({
            totalJoints: 0,
            totalLength: 0,
            averageLength: 0,
            totalWeight: 0
        });
    }, []);

    const handleTallyDelete = useCallback(async (deletedTally, parentRun) => {
        console.log('🗑️ Tally record deleted:', deletedTally);
        // Recalculate statistics after deletion
        if (parentRun) {
            await handleCasingRunSelect(parentRun);
        }
    }, [handleCasingRunSelect]);

    const handleWellChange = useCallback((wellId) => {
        setSearchParams({ wellId });
    }, [setSearchParams]);

    // Custom tab content renderer
    const renderCustomTabContent = useCallback((activeTab, selectedParent, childData, wellId) => {
        switch (activeTab) {
            case 'attachments':
                // Ensure the parent record has WellID for AttachmentManager
                const parentRecordWithWellId = selectedParent ? {
                    ...selectedParent,
                    WellID: selectedParent.WellID || wellId
                } : null;

                // Debug logging to trace WellID values
                console.log('🔧 Casing_v2 - AttachmentManager props:', {
                    selectedParent,
                    wellId,
                    parentRecordWithWellId,
                    wellIdFromParent: selectedParent?.WellID,
                    wellIdFromProps: wellId,
                    finalWellId: parentRecordWithWellId?.WellID,
                    parentType: 'Casing',
                    parentIdField: 'CasingRunID'
                });

                return (
                    <div>
                        {selectedParent && (
                            <div className="attachment-context-header" style={{
                                background: '#f8f9fa',
                                border: '1px solid #e9ecef',
                                borderRadius: '6px',
                                padding: '12px',
                                marginBottom: '16px',
                                color: '#495057'
                            }}>
                                <p style={{ margin: 0, fontSize: '14px' }}>
                                    <strong>Attachments for: {selectedParent.CasingString || `Casing Run ${selectedParent.CasingRunID}`}</strong>
                                    {selectedParent.RunDate && ` • Run Date: ${new Date(selectedParent.RunDate).toLocaleDateString()}`}
                                    {selectedParent.Size && ` • Size: ${selectedParent.Size}"`}
                                    {selectedParent.Grade && ` • Grade: ${selectedParent.Grade}`}
                                </p>
                            </div>
                        )}
                        <AttachmentManager
                            parentRecord={parentRecordWithWellId}
                            parentType="Casing"
                            parentIdField="CasingRunID"
                            allowedFileTypes={CASING_ALLOWED_FILE_TYPES}
                            maxFileSize={CASING_MAX_FILE_SIZE}
                            className="casing-attachments"
                        />
                    </div>
                );
            default:
                return null; // Let WellData handle the default 'details' tab
        }
    }, []);

    return (
        <div className="casing-v2-container">
            <WellData
                config={wellDataConfig}
                wellId={wellId}
                onWellChange={handleWellChange}
                // Event handlers
                onParentSelect={handleCasingRunSelect}
                onChildSelect={handleTallySelect}
                onParentSave={handleCasingRunSave}
                onChildSave={handleTallySave}
                onParentDelete={handleCasingRunDelete}
                onChildDelete={handleTallyDelete}
                renderCustomTabContent={renderCustomTabContent}
            />

            {/* Statistics Panel */}
            <div className="casing-stats-panel">
                <div className="stats-header">
                    <h3>📈 Casing Run Statistics</h3>
                    <button
                        className="diagram-btn"
                        onClick={() => setShowDiagramModal(true)}
                    >
                        📊 View Diagram
                    </button>
                </div>
                <div className="summary-stats">
                    <div className="stat-card">
                        <div className="stat-icon">🔗</div>
                        <div className="stat-content">
                            <div className="stat-value">{tallyStats.totalJoints}</div>
                            <div className="stat-label">Total Joints</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">📏</div>
                        <div className="stat-content">
                            <div className="stat-value">{tallyStats.totalLength.toFixed(2)} m</div>
                            <div className="stat-label">Total Length</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">⚖️</div>
                        <div className="stat-content">
                            <div className="stat-value">{tallyStats.totalWeight.toFixed(2)} kg</div>
                            <div className="stat-label">Total Weight</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">📊</div>
                        <div className="stat-content">
                            <div className="stat-value">{tallyStats.averageLength.toFixed(2)} m</div>
                            <div className="stat-label">Avg Joint Length</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Diagram Modal */}
            {showDiagramModal && (
                <div className="modal-overlay" onClick={() => setShowDiagramModal(false)}>
                    <div className="modal-content diagram-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>📊 Casing String Diagram</h2>
                            <button 
                                className="modal-close"
                                onClick={() => setShowDiagramModal(false)}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="full-diagram">
                                <div className="diagram-placeholder-large">
                                    <div className="placeholder-content">
                                        <div className="placeholder-icon">📊</div>
                                        <h3>Full Casing String Diagram</h3>
                                        <p>Detailed casing string visualization would be displayed here</p>
                                        <div className="diagram-features">
                                            <span>• Joint-by-joint visualization</span>
                                            <span>• Depth markers</span>
                                            <span>• Grade and size indicators</span>
                                            <span>• Connection details</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Casing_v2; 